/**
 * Public provenance viewer — Phase 3, Slice 2 (EXIT CRITERION).
 *
 * A PUBLIC, unauthenticated page (registered on rootRoute beside /design and
 * /signup — NOT behind the _app auth gate, so no sidebar / app chrome). A buyer
 * scans a batch QR with their phone camera, lands here, and sees the batch's
 * full provenance trail with cryptographic verification.
 *
 * The page renders:
 *   - a prominent verification badge: green "Cryptographically verified" when
 *     `verification.allValid`, amber "Pending on-chain anchor" otherwise;
 *   - a timeline (Stepper) of trace events with per-event anchor StatusPill,
 *     event kind, occurred-at, a truncated content hash, and a tamper warning
 *     if the stored payload no longer re-hashes to the recorded hash.
 *
 * Verification model (per the plan): the server recomputes keccak256 from the
 * stored payload and reports `contentHashValid`; on-chain anchoring is DEFERRED
 * (Phase-2 chain workers + KMS), so anchorStatus is 'pending' until those land
 * and back-fill — the badge upgrades to "anchored" with zero client change.
 *
 * Data is fetched with the hand-rolled UNAUTHENTICATED client in
 * src/api/provenance.ts (no Authorization header). i18n keys live under
 * verify.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ColorModeToggle,
  Container,
  Divider,
  Icon,
  Stack,
  StatusPill,
  Stepper,
  Text,
  useColorMode,
  mdSysColor,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  getPublicProvenance,
  type PublicProvenanceDto,
  type PublicProvenanceEventDto,
} from '../api/provenance';
import { groupDigits } from '../lib/format';

const VALID_KINDS = new Set(['batch', 'contribution', 'transaction']);
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Format an ISO-8601 timestamp to a readable local date-time, guarding NaN. */
function formatOccurredAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return dateTimeFormatter.format(parsed);
}

/** Truncate a 0x-prefixed hex hash to head…tail for compact display. */
function shortHash(hex: string): string {
  if (hex.length <= 18) return hex;
  return `${hex.slice(0, 10)}…${hex.slice(-6)}`;
}

/**
 * Render a redacted-payload value for display. Objects are JSON-stringified as
 * before; otherwise we stringify and, ONLY for a pure numeric value whose
 * integer part is >= 4 digits (>= 1000, e.g. a batch quantity/weight in kg),
 * add thousands separators for readability. Every other value (hashes, UUIDs,
 * codes, booleans, small numbers) is returned verbatim — output-preserving.
 */
function formatPayloadValue(value: unknown): string {
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  const str = String(value);
  if (/^-?\d+(\.\d+)?$/.test(str)) {
    const intDigits = (str.replace(/^-/, '').split('.')[0] ?? '');
    if (intDigits.length >= 4) {
      return str.startsWith('-') ? `-${groupDigits(str.slice(1))}` : groupDigits(str);
    }
  }
  return str;
}

export function VerifyProvenanceScreen() {
  const { t } = useTranslation();
  // Manual route → read params loosely; the route validates shape via the
  // guard below, not via a generated route-id type.
  const params = useParams({ strict: false }) as { kind?: string; id?: string };
  const kind = params.kind ?? '';
  const id = params.id ?? '';

  const shapeValid = VALID_KINDS.has(kind) && UUID_RE.test(id);

  const query = useQuery({
    queryKey: ['provenance', kind, id],
    queryFn: () => getPublicProvenance(kind, id),
    enabled: shapeValid,
    retry: false,
  });

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Text variant="headline-medium" as="h1">
              {t('verify.title')}
            </Text>
            <Box sx={{ mt: 0.5 }}>
              <Text variant="body-medium" color="text.secondary">
                {t('verify.subtitle')}
              </Text>
            </Box>
          </Box>
          <ColorModeToggle />
        </Box>

        {!shapeValid ? (
          <Alert severity="error">{t('verify.badRequest')}</Alert>
        ) : query.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : query.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => query.refetch()}>
                {t('verify.retry')}
              </Button>
            }
          >
            {t('verify.loadError', {
              error:
                query.error instanceof Error
                  ? query.error.message
                  : String(query.error),
            })}
          </Alert>
        ) : (
          <ProvenanceBody data={query.data} id={id} t={t} />
        )}
      </Stack>
    </Container>
  );
}

interface ProvenanceBodyProps {
  data: PublicProvenanceDto;
  id: string;
  t: TFunction;
}

function ProvenanceBody({ data, id, t }: ProvenanceBodyProps) {
  const { events, verification } = data;
  const allValid = verification.allValid;

  return (
    <Stack spacing={3}>
      {/* Prominent verification badge */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                bgcolor: allValid ? 'success.main' : 'warning.main',
                color: allValid ? 'success.contrastText' : 'warning.contrastText',
              }}
            >
              <Icon name={allValid ? 'verified' : 'hourglass_empty'} color="inherit" />
            </Box>
            <Stack spacing={0.5}>
              <Text variant="title-large" as="span">
                {allValid
                  ? t('verify.badge.verified')
                  : t('verify.badge.pending')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {allValid
                  ? t('verify.badge.verifiedBody')
                  : t('verify.badge.pendingBody')}
              </Text>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between' }}
          >
            <Text variant="body-small" color="text.secondary">
              {t(`verify.subjectKind.${data.subjectKind}`)} ·{' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {id.slice(0, 8)}
              </Box>
            </Text>
            <Text variant="body-small" color="text.secondary">
              {t('verify.counts', {
                anchored: verification.anchoredCount,
                pending: verification.pendingCount,
              })}
            </Text>
          </Stack>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Stack spacing={1.5}>
        <Text variant="title-large" as="h2">
          {t('verify.timeline.title', { count: events.length })}
        </Text>

        {events.length === 0 ? (
          <Card>
            <CardContent>
              <Text variant="body-medium" color="text.secondary">
                {t('verify.timeline.empty')}
              </Text>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {/* A horizontal progress stepper across event kinds, plus per-event
                cards beneath it for the detail (hash, status, payload). */}
            <Box sx={{ display: { xs: 'none', sm: 'block' }, py: 1 }}>
              <Stepper
                steps={events.map((e) => t(`trace.eventKind.${e.eventKind}`, e.eventKind))}
                activeStep={events.length - 1}
              />
            </Box>
            {events.map((event, idx) => (
              <ProvenanceEventCard key={`${event.contentHashHex}-${idx}`} event={event} t={t} />
            ))}
          </Stack>
        )}
      </Stack>

      <Text variant="body-small" color="text.secondary" align="center">
        {t('verify.footer')}
      </Text>

      {/* Onward CTA — this is a public page reached by scanning a batch QR, so a
          buyer/visitor has no path forward. A gentle invite to join (task 8).
          Mode-aware container tint via mdSysColor; links to the public /signup. */}
      <JoinCta t={t} />
    </Stack>
  );
}

/**
 * Gentle "join us" call-to-action for the public verifier. Not pushy — a single
 * line + a primary Join button and a secondary Learn-more. Both link to /signup
 * (the public onboarding route on rootRoute) via TanStack Link.
 */
function JoinCta({ t }: { t: TFunction }) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <Card sx={{ bgcolor: sys.secondaryContainer, color: sys.onSecondaryContainer }}>
      <CardContent>
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
            <Text variant="title-medium" as="h2">
              {t('verify.cta.title')}
            </Text>
            <Text variant="body-medium" color="inherit">
              {t('verify.cta.body')}
            </Text>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ alignItems: 'center' }}
          >
            <Link to="/signup" style={{ textDecoration: 'none' }}>
              <Button variant="contained">{t('verify.cta.join')}</Button>
            </Link>
            <Link to="/signup" style={{ textDecoration: 'none' }}>
              <Button variant="text" color="inherit">
                {t('verify.cta.learnMore')}
              </Button>
            </Link>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface ProvenanceEventCardProps {
  event: PublicProvenanceEventDto;
  t: TFunction;
}

function ProvenanceEventCard({ event, t }: ProvenanceEventCardProps) {
  const payloadEntries = Object.entries(event.redactedPayload ?? {});

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Icon name="event" />
              <Text variant="title-medium" as="span">
                {t(`trace.eventKind.${event.eventKind}`, event.eventKind)}
              </Text>
            </Stack>
            <StatusPill
              variant={event.anchorStatus === 'anchored' ? 'sold' : 'due'}
              icon={event.anchorStatus === 'anchored' ? 'check_circle' : 'hourglass_empty'}
            >
              {event.anchorStatus === 'anchored'
                ? t('verify.anchor.anchored')
                : t('verify.anchor.pending')}
            </StatusPill>
          </Box>

          <Text variant="body-medium" color="text.secondary">
            {formatOccurredAt(event.occurredAt)}
          </Text>

          {/* Tamper-evidence: warn loudly if the stored payload no longer
              re-hashes to the recorded content hash. */}
          {event.contentHashValid ? null : (
            <Alert severity="error">{t('verify.tampered')}</Alert>
          )}

          {payloadEntries.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                alignItems: 'baseline',
                gap: 0.5,
              }}
            >
              {payloadEntries.map(([key, value]) => (
                <Box key={key} sx={{ display: 'contents' }}>
                  <Text variant="body-small" color="text.secondary">
                    {key}
                  </Text>
                  <Text variant="body-small" color="text.primary">
                    {formatPayloadValue(value)}
                  </Text>
                </Box>
              ))}
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
            }}
          >
            <Icon name={event.contentHashValid ? 'fingerprint' : 'error'} size={20} />
            <Text variant="label-small" color="text.secondary">
              {t('verify.hashLabel')}
            </Text>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
              {shortHash(event.contentHashHex)}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
